import { ContextError } from 'errors/ContextError';
import { PeopleTabVal } from 'pages/SearchPeoplePage/SearchPeoplePage';
import { PeopleSearchFilters } from 'redux/people/reducer';
import { GroupSearchFilters, GroupTab } from 'redux/groups/reducer';
import { ProfileEditSection } from 'pages/EditProfilePage/EditProfilePage';
import { OAuthProviderName } from 'types';

enum ROUTES {
    LANDING = '/',

    // Auth
    LOGIN = '/i/auth',
    OAUTH_CALLBACK = '/i/auth/:provider',
    JOIN = '/i/join',
    ONBOARDING = '/i/onboarding',
    ONBOARDING_LANGUAGES = '/i/onboarding/languages',

    HOME = '/i/feed',
    HOME_POST_PHOTO = '/i/feed/:feedOwnerId/post/:postId/photo/:photoId',

    PRIVACY = '/i/privacy',
    SUPPORT = '/i/support',
    TOS = '/i/tos',
    ABOUT = '/i/about',
    ALERTS = '/i/alerts',

    BOOKMARKS = '/i/bookmarks',

    EDIT_PROFILE = '/i/:username/edit/info',
    EDIT_PROFILE_INTERESTS = '/i/:username/edit/interests',
    EDIT_PROFILE_WANT_TO_MEET = '/i/:username/edit/matching',

    EMAIL_VERIFICATION = '/i/verification',
    RESET_PASSWORD = '/i/reset-password',

    FRIENDS = '/i/friends',
    FRIENDS_BIRTHDAYS = '/i/friends?section=birthdays',
    FRIENDS_REQUEST = '/i/friends?section=requests',

    GROUP = '/i/groups/:groupId',
    GROUP_POST_PHOTO = '/i/groups/:groupId/post/:postId/photo/:photoId',

    MESSAGES_HOME = '/i/messages',
    MESSAGES_THREAD = '/i/messages/:contactUid',
    MESSAGES_THREAD_PHOTO = '/i/messages/:contactUid/thread/:threadId/message/:messageId/photo/:photoId',

    POST = '/i/posts/:feedOwnerId/post/:postId',
    POST_PHOTO = '/i/posts/:feedOwnerId/post/:postId/photo/:photoId',

    USER_PROFILE = '/:username',
    // Insert :uid here to avoid extra lookups if :username is a username
    USER_PROFILE_ALBUM_LIST = '/:username/u/:uid/albums',
    USER_PROFILE_ALBUM_LIST_RECENT_PHOTO = '/:username/u/:uid/albums/photo/:photoId',
    USER_PROFILE_ALBUM = '/:username/u/:uid/albums/:albumId',
    USER_PROFILE_ALBUM_PHOTO = '/:username/u/:uid/albums/:albumId/photo/:photoId',
    USER_PROFILE_CAROUSEL_PHOTO = '/:username/u/:uid/album/:albumId/photo/:photoId',
    USER_PROFILE_RECENT_PHOTO = '/:username/u/:uid/photo/:photoId',
    USER_PROFILE_POST_PHOTO = '/:username/u/:uid/post/:postId/photo/:photoId',
    // Redirects to USER_PROFILE
    USER_PROFILE_UID = '/i/user/:uid',

    SEARCH_GROUPS = '/i/search/groups/:tab',
    SEARCH_PEOPLE = '/i/search/people/:tab',

    SETTINGS = '/i/settings',
    SETTINGS_FILTERS = '/i/settings?section=filters',
    SETTINGS_NOTIFICATIONS = '/i/settings?section=notifications',
    SETTINGS_PRIVACY = '/i/settings?section=privacy',

    MODERATION_REPORTS = '/i/moderation/reports',
    MODERATION_LIST = '/i/moderation/list',
    MODERATION_FLAGGED_CONTENT = '/i/moderation/flagged-content',
    MODERATION_LOG = '/i/moderation/log',
    MODERATION_RECENT = '/i/moderation/recent',
    MODERATION_SETTINGS = '/i/moderation/settings',
    MODERATION_USER_SEARCH = '/i/moderation/user-search',
    MODERATION_USER = '/:id/moderate',
    MODERATION_MESSAGES = '/i/moderation/:userId/messages',
    MODERATION_MESSAGE_THREAD = '/i/moderation/:userId/messages/:contactUid',

    AD_TEST_PUBSTREAM = '/i/ads/pubstream',
    AD_TEST_ADSENSE = '/i/ads/adsense',
}

export const makeProfileURL = (username: string): string => ROUTES.USER_PROFILE.replace(':username', username);

// Wherever possible, makeProfileURL should be used instead for consistency
export const makeProfileUidRedirectURL = (uid: string): string => ROUTES.USER_PROFILE_UID.replace(':uid', uid);

export const makeAlbumPhotoURL = ({
    username,
    uid,
    photoId,
    albumId,
}: {
    username: string;
    uid: string;
    photoId: string;
    albumId?: string;
}): string => {
    const url = ROUTES.USER_PROFILE_ALBUM_PHOTO.replace(':username', username)
        .replace(':uid', uid)
        .replace(':photoId', photoId);

    return albumId ? url.replace(':albumId', albumId) : url;
};

export const makeAlbumListURL = (username: string, uid: string): string =>
    ROUTES.USER_PROFILE_ALBUM_LIST.replace(':username', username).replace(':uid', uid);

export const makeAlbumListRecentPhotoURL = (username: string, uid: string, photoId: string): string =>
    ROUTES.USER_PROFILE_ALBUM_LIST_RECENT_PHOTO.replace(':username', username)
        .replace(':uid', uid)
        .replace(':photoId', photoId);

export const makeAlbumURL = (username: string, uid: string, albumId: string): string =>
    ROUTES.USER_PROFILE_ALBUM.replace(':username', username).replace(':uid', uid).replace(':albumId', albumId);

export const makeThreadURL = (contactUid: string): string => ROUTES.MESSAGES_THREAD.replace(':contactUid', contactUid);

export const makeMessagePhotoURL = ({
    contactUid,
    threadId,
    messageId,
    photoId,
}: {
    contactUid: string;
    threadId: string;
    messageId: string;
    photoId: string;
}): string =>
    ROUTES.MESSAGES_THREAD_PHOTO.replace(':contactUid', contactUid)
        .replace(':threadId', threadId)
        .replace(':photoId', photoId)
        .replace(':messageId', messageId);

export const makeGroupURL = (groupId: string): string => ROUTES.GROUP.replace(':groupId', groupId);

export const makeGroupSearchURL = (
    filters: Partial<GroupSearchFilters> | null = null,
    tab: GroupTab = 'all',
): Partial<Location> => {
    const URLParams = new URLSearchParams(filters ? { filters: JSON.stringify(filters) } : undefined);

    return {
        pathname: ROUTES.SEARCH_GROUPS.replace(':tab', tab),
        search: '?' + URLParams.toString(),
    };
};

export const makeSinglePostURL = (feedOwnerId: string, id: string): string =>
    ROUTES.POST.replace(':feedOwnerId', feedOwnerId).replace(':postId', id);

// Assume that '/photo/:photoId' is the same across all photo modal URLs
export const replaceModalPhotoId = (url: string, newPhotoId: string): string =>
    url.replace(/photo\/\w+/, `photo/${newPhotoId}`);

export const makeSinglePostPhotoURL = (feedOwnerId: string, postId: string, photoId: string): string =>
    ROUTES.POST_PHOTO.replace(':feedOwnerId', feedOwnerId).replace(':postId', postId).replace(':photoId', photoId);

export const makeProfilePostPhotoURL = (username: string, uid: string, postId: string, photoId: string): string =>
    ROUTES.USER_PROFILE_POST_PHOTO.replace(':username', username)
        .replace(':uid', uid)
        .replace(':postId', postId)
        .replace(':photoId', photoId);

export const makeHomePostPhotoURL = (feedOwnerId: string, postId: string, photoId: string): string =>
    ROUTES.HOME_POST_PHOTO.replace(':feedOwnerId', feedOwnerId).replace(':postId', postId).replace(':photoId', photoId);

export const makeGroupPostPhotoURL = (groupId: string, postId: string, photoId: string): string =>
    ROUTES.GROUP_POST_PHOTO.replace(':groupId', groupId).replace(':postId', postId).replace(':photoId', photoId);

export const makePostPhotoURL = (
    currentPathname: string,
    feedOwnerId: string,
    postId: string,
    photoId: string,
    uid?: string,
    username?: string,
): string => {
    if (currentPathname.includes(ROUTES.HOME)) {
        return makeHomePostPhotoURL(feedOwnerId, postId, photoId);
    }

    if (currentPathname.includes('/i/groups')) {
        return makeGroupPostPhotoURL(feedOwnerId, postId, photoId);
    }

    if (currentPathname.includes('/i/posts')) {
        return makeSinglePostPhotoURL(feedOwnerId, postId, photoId);
    }

    if (uid) {
        if (!username) {
            throw new Error('Username is required to make a profile post photo URL');
        }

        return makeProfilePostPhotoURL(username, uid, postId, photoId);
    }

    throw new ContextError('Could not determine post photo URL', {
        currentPathname,
        feedOwnerId,
        postId,
        photoId,
    });
};

export const makeProfileRecentPhotoURL = (username: string, uid: string, photoId: string): string =>
    ROUTES.USER_PROFILE_RECENT_PHOTO.replace(':username', username).replace(':uid', uid).replace(':photoId', photoId);

export const makeProfileCarouselPhotoURL = (username: string, uid: string, albumId: string, photoId: string): string =>
    ROUTES.USER_PROFILE_CAROUSEL_PHOTO.replace(':username', username)
        .replace(':uid', uid)
        .replace(':albumId', albumId)
        .replace(':photoId', photoId);

export const makeUserModerationURL = (userId: string): string => ROUTES.MODERATION_USER.replace(':id', userId);

export const makeMessagesModerationURL = (userId: string): string =>
    ROUTES.MODERATION_MESSAGES.replace(':userId', userId);

export const makeUserMessagesModerationURL = (userId: string, contactUid: string): string =>
    ROUTES.MODERATION_MESSAGE_THREAD.replace(':userId', userId).replace(':contactUid', contactUid);

interface ModerationUserSearchURLParams {
    searchText: string;
    active?: boolean;
}

export const makeModerationUserSearchURL = ({
    searchText,
    active,
}: ModerationUserSearchURLParams): Partial<Location> => {
    const URLParams = new URLSearchParams({ searchText, active: String(Boolean(active)) });

    return {
        pathname: ROUTES.MODERATION_USER_SEARCH,
        search: '?' + URLParams.toString(),
    };
};

export const makePeopleSearchURL = (
    filters: Partial<PeopleSearchFilters> | null = null,
    tab: PeopleTabVal = 'all',
): Partial<Location> => {
    const URLParams = new URLSearchParams(filters ? { filters: JSON.stringify(filters) } : undefined);

    return {
        pathname: ROUTES.SEARCH_PEOPLE.replace(':tab', tab),
        search: '?' + URLParams.toString(),
    };
};

interface RecentSearchURLParams {
    contentType: string;
    status: string;
}

export const makeRecentSearchURL = ({ contentType, status }: RecentSearchURLParams): Partial<Location> => {
    const URLParams = new URLSearchParams({ contentType, status });

    return {
        pathname: ROUTES.MODERATION_RECENT,
        search: '?' + URLParams.toString(),
    };
};

export const makeEditProfileURL = (username: string, section: ProfileEditSection): string => {
    switch (section) {
        case 'info':
            return ROUTES.EDIT_PROFILE.replace(':username', username);
        case 'interests':
            return ROUTES.EDIT_PROFILE_INTERESTS.replace(':username', username);
        case 'matching':
            return ROUTES.EDIT_PROFILE_WANT_TO_MEET.replace(':username', username);
    }
};

export const makeOauthRedirectUri = (provider: OAuthProviderName): string =>
    ROUTES.OAUTH_CALLBACK.replace(':provider', provider);

export default ROUTES;
